import * as React from "react";
import {
  Badge as PresetBadge,
  badgeVariants as presetBadgeVariants,
  type BadgeColor,
  type BadgeVariant,
} from "@volca/ui/components/ui/badge-base";
import { cn } from "@volca/ui/lib/utils";

const SIZES = {
  sm: "h-auto min-h-5",
  md: "h-auto min-h-6 px-2.5",
  lg: "h-auto min-h-7 px-3 text-sm",
} as const;

type BadgeSize = keyof typeof SIZES;

interface BadgeVariantProps {
  variant?: BadgeVariant | null;
  color?: BadgeColor | null;
  size?: BadgeSize | null;
}

const badgeVariants = ({
  variant,
  color,
  size,
  className,
}: BadgeVariantProps & { className?: string } = {}) =>
  presetBadgeVariants({
    variant,
    color,
    className: cn(
      SIZES[size ?? "md"],
      "in-data-[slot=badge-group]:rounded-none in-data-[slot=badge-group]:border-0",
      className,
    ),
  });

export interface BadgeProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "color">,
    BadgeVariantProps {}

const Badge = React.forwardRef<HTMLDivElement, BadgeProps>(
  ({ className, variant, color, size, ...props }, ref) => (
    <PresetBadge
      variant={variant ?? "default"}
      color={color}
      {...props}
      render={(renderProps) => (
        <div
          {...renderProps}
          ref={ref}
          className={badgeVariants({ variant, color, size, className })}
        />
      )}
    />
  ),
);

Badge.displayName = "Badge";

export { Badge, badgeVariants, type BadgeColor, type BadgeVariant };
